import * as React from "react";
import { Alert, Skeleton, Snackbar, Stack, TextField } from "@mui/material";
import { useLanguage } from "./Language";
import { ObrPinGeneration, useOBRSettings } from "./OBRSettings";
import axios from "axios";
import { FormProvider, useForm } from "react-hook-form";
import { FormDialog } from "@tlc/ui/dialog/FormDialog";
import { useEffect, useMemo, useState } from "react";
import { ConfirmPINField } from "./pages/obr/PinField";
import { useErrorDialog } from "@tlc/ui/dialog/error/ErrorDialogContext";

export interface PINResetFormProps {
  borrowerNumber: number;
  verificationCode: string;
  open: boolean;
  onClose: () => void;
}

export interface ResetPinRequestBody {
  borrowerNumber: number;
  verificationCode: string;
  newPIN: string;
  confirmNewPIN: string;
}

export const PINResetForm: React.FC<PINResetFormProps> = (props) => {
  const { borrowerNumber, verificationCode, open, onClose } = props;
  const formMethods = useForm<ResetPinRequestBody>({
    defaultValues: {
      borrowerNumber: props.borrowerNumber,
      verificationCode: props.verificationCode,
      newPIN: "",
      confirmNewPIN: "",
    },
  });

  const { handleSubmit, setFocus, reset } = formMethods;

  const { handleRequestError } = useErrorDialog();

  useEffect(() => {
    if (open) {
      setFocus("newPIN");
    }
    reset();
  }, [open, setFocus, reset]);

  const { obrConfiguration } = useOBRSettings();

  const [userVerified, setUserVerified] = useState<boolean | null>(null);
  useEffect(() => {
    if (!borrowerNumber) {
      throw "borrowerNumber missing";
    }

    if (!verificationCode) {
      throw "verificationCode missing";
    }
    axios
      .get<boolean>("/account/checkVerificationCode", {
        params: {
          borrowerNumber,
          verificationCode,
        },
      })
      .then(({ data: verified }) => {
        setError(verified !== true);
        setUserVerified(verified);
      })
      .catch((e: unknown) => {
        setUserVerified(false);
        handleRequestError(e);
      });
  }, [borrowerNumber, verificationCode]);

  const loading = useMemo(
    () =>
      userVerified === null ||
      userVerified === undefined ||
      obrConfiguration === null ||
      obrConfiguration === undefined,
    [userVerified, obrConfiguration],
  );

  const { translate } = useLanguage();

  const pinLengthRequired =
    obrConfiguration?.obrEnabled &&
    obrConfiguration.howToGeneratePin === ObrPinGeneration.None
      ? obrConfiguration.pinLengthRequired
      : undefined;

  const [error, setError] = React.useState<boolean>(false);
  const [success, setSuccess] = React.useState<boolean>(false);

  const onSubmit = (data: ResetPinRequestBody) => {
    axios
      .post<string>("/account/completePINReset", data)
      .then((response) => {
        if (response.status === 200) {
          setSuccess(true);
          onClose();
        }
      })
      .catch((error) => {
        handleRequestError(error);
        // frontend validation should cover anticipated errors
        // handleRequestError should handle the rest
      });
  };

  const handleCloseSnackbar = (
    _event: React.SyntheticEvent | Event,
    _reason?: string,
  ) => {
    setSuccess(false);
  };

  return (
    <React.Fragment>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={success}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={handleCloseSnackbar}
          sx={{ width: "100%" }}
        >
          {translate("pinChangedMsg")}
        </Alert>
      </Snackbar>
      <FormDialog
        maxWidth="xs"
        confirmButtonText={translate("resetPIN")}
        submitButtonDisabled={!userVerified}
        headline={translate("resetPIN")}
        formId="pin-reset-form"
        open={open}
        onClose={onClose}
      >
        {!success && !loading && (
          <FormProvider {...formMethods}>
            <form
              id="pin-reset-form"
              onSubmit={handleSubmit(onSubmit)}
              noValidate
            >
              <Stack
                direction="column"
                sx={{
                  gap: 2,
                  paddingTop: 1,
                }}
              >
                {error && (
                  <Alert severity="error">{translate("expiredLinkMsg")}</Alert>
                )}
                <ConfirmPINField<ResetPinRequestBody>
                  name={"newPIN"}
                  confirmName={"confirmNewPIN"}
                  pinLabel={translate("newPINMsg")}
                  confirmPinLabel={translate("confirmNewPINMsg")}
                  pinMismatchError={translate("pinMismatchMsg")}
                  disabled={!userVerified}
                  pinLengthRequired={pinLengthRequired}
                />
              </Stack>
            </form>
          </FormProvider>
        )}
        {loading && (
          <Stack
            direction="column"
            sx={{
              gap: 2,
              paddingTop: 1,
            }}
          >
            <Skeleton>
              <TextField helperText=" "></TextField>
            </Skeleton>
            <Skeleton>
              <TextField helperText=" "></TextField>
            </Skeleton>
          </Stack>
        )}
      </FormDialog>
    </React.Fragment>
  );
};
